import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { OrderStatusKeyEnum } from '@core/enums/order-status-key.enum';
import { PermissionsService } from '@core/services/permissions.service';
import { ROOT_SCOPE_VARIABLES } from '@core/settings/root-scope.settings';
import { DragObjectTypeEnum } from '../../enums/drag-object-type.enum';
import { orderStatusColorsSet } from '../../enums/order-status-color.map';
import { DayModel } from '../../models/day-model';
import { DriverVehiclePairModel } from '../../models/driver-vehicle-pair.model';
import { OrderDragModel } from '../../models/order-drag-and-drop.model';
import { OrderModel } from '../../models/order.model';
import { RouteModel } from '../../models/route.model';
import { PlannerDraggingManagerService } from '../../services/planner-draggin-manager.service';
import { BusinessObjectType } from '@core/enums/business-object-type.enum';
import { AddNewAbsenceEvent } from '../empty-day-box/empty-day-box.component';

const ExcludedFromEditing = [OrderStatusKeyEnum.COMPLETED, OrderStatusKeyEnum.INVOICED];

@Component({
  selector: 'order-box',
  standalone: false,
  templateUrl: './order-box.component.html',
  styleUrls: ['./order-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderBoxComponent implements OnChanges, OnInit {
  @Input() order!: OrderModel;
  @Input() dataObjectType: DragObjectTypeEnum = DragObjectTypeEnum.SHIPMENT;
  @Input() route?: RouteModel;
  @Input() day?: DayModel;
  @Input() driver?: DriverVehiclePairModel;
  @Input() isEmbeddedInRoute = false;
  @Input() showDateSection = false;
  @Input() showStatusSection = false;
  @Input() isInPhantomRoute? = false;


  @Output() editShipment = new EventEmitter<OrderModel>();
  @Output() editOrder = new EventEmitter<OrderModel>();
  @Output() editRoute = new EventEmitter<RouteModel>();
  @Output() sendRouteToDriver = new EventEmitter<RouteModel>();
  @Output() showConflicts = new EventEmitter<DayModel>();
  @Output() addNewAbsence = new EventEmitter<AddNewAbsenceEvent>;

  @ViewChild('menuTrigger') contextMenuTrigger!: MatMenuTrigger;

  isDragAllowed = false;
  isOrderDragged = false;
  styleChangeTimeout?: NodeJS.Timeout;
  statusColor = "";
  hasShowOrderPermission: any;
  hasShowShipmentPermission: any;

  isShipment = true;
  ROOT_SCOPE_VARIABLES = ROOT_SCOPE_VARIABLES;

  businessObjectType = BusinessObjectType;
  PERM = ROOT_SCOPE_VARIABLES.PERMISSIONS.UNAVAILABILITY;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly _permissionService: PermissionsService,
    private readonly _draggingManager: PlannerDraggingManagerService,
  ) { }

  ngOnInit(): void {
    this.hasShowOrderPermission = this._permissionService.isPermissionEnabled(ROOT_SCOPE_VARIABLES.PERMISSIONS.PLANNING_DAILYPLAN.PLANNING.SHOWORDER);
    this.hasShowShipmentPermission = this._permissionService.isPermissionEnabled(ROOT_SCOPE_VARIABLES.PERMISSIONS.PLANNING_DAILYPLAN.PLANNING.SHOWSHIPMENT);
    this.isShipment = this.dataObjectType === DragObjectTypeEnum.SHIPMENT;

    this.isDragAllowed = !ExcludedFromEditing.includes(this.order.StatusDictionary?.KeyName as OrderStatusKeyEnum) && !this.isInPhantomRoute;
  }

  ngOnChanges(): void {
    if (this.order) {
      if (this.order.StatusDictionary && this.order.StatusDictionary.KeyName) {
        this.statusColor = orderStatusColorsSet[this.order.StatusDictionary.KeyName as OrderStatusKeyEnum];
      }
    }
  }

  dragIsStarting($event: DragEvent): void {
    if (!this.isDragAllowed) {
      return
    }

    const dragModel = {
      order: this.order,
      day: this.day,
      route: this.route,
      driver: this.driver
    } as OrderDragModel;

    this._draggingManager.setDragInfoData($event, dragModel, this.route?.routeId, this.dataObjectType);
    this._draggingManager.isDragInProgress$.next(true);

    this.styleChangeTimeout = setTimeout(() => {
      this.isOrderDragged = true;
      this.changeDetectorRef.detectChanges();
    }, 0);
  }


  dragEnded(_: any) {
    this.isOrderDragged = false;
    this._draggingManager.isDragInProgress$.next(false);

    if (this.styleChangeTimeout) {
      clearTimeout(this.styleChangeTimeout);
    }
  }

  editClick(): void {
    if (this.isShipment && this.hasShowShipmentPermission) {
      this.editShipment.emit(this.order);
    } else if (this.hasShowOrderPermission) {
      this.editOrder.emit(this.order);
    }
  }

  editRouteClick(): void {
    if (this.route) {
      this.editRoute.emit(this.route);
    }
  }

  sendRouteToDriverClick() {
    this.sendRouteToDriver.emit(this.route);
  }

  contextClick($event: MouseEvent): void {
    if (!$event.shiftKey) {
      $event.preventDefault();
      this.contextMenuTrigger.openMenu();
    }
  }

  showConflictsClick() {
    this.showConflicts.emit(this.day);
  }

  onAddAbsenceClick(objectType: BusinessObjectType, objectId: number): void {
      this.addNewAbsence.emit({ objectType, objectId });
  }
}
